import { ApiBase } from './base';
import { Project } from '../../types';

export class ApiProjects extends ApiBase {
  // ============ PROJECT MANAGEMENT ============
  
  /**
   * Get all projects, optionally filtered by condo
   */
  async getProjects(condoId?: string): Promise<Project[]> {
    const url = condoId ? `/projects?condoId=${condoId}` : '/projects';
    const projects = await this.request<Project[]>(url);
    // Convert date strings to Date objects
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get a single project by ID
   */
  async getProject(id: string): Promise<Project> {
    const project = await this.request<Project>(`/projects/${id}`);
    return {
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    };
  }

  /**
   * Get projects by condo ID
   */
  async getProjectsByCondo(condoId: string): Promise<Project[]> {
    const projects = await this.request<Project[]>(`/projects/condo/${condoId}`);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Create a new project
   */
  async createProject(project: Omit<Project, 'id' | 'createdAt' | 'updatedAt'>): Promise<Project> {
    const newProject = await this.request<Project>('/projects', {
      method: 'POST',
      body: JSON.stringify({
        ...project,
        // Backend Project.startDate/endDate are LocalDate (calendar dates,
        // not instants) — send YYYY-MM-DD, not a full ISO timestamp, or
        // Jackson rejects the body. ADR-147.
        startDate: project.startDate.toISOString().slice(0, 10),
        endDate: project.endDate.toISOString().slice(0, 10),
      }),
    });
    return {
      ...newProject,
      startDate: new Date(newProject.startDate),
      endDate: new Date(newProject.endDate),
      createdAt: new Date(newProject.createdAt),
      updatedAt: new Date(newProject.updatedAt)
    };
  }

  /**
   * Update an existing project
   */
  async updateProject(id: string, project: Partial<Project>): Promise<Project> {
    // Prepare the update payload
    const updatePayload: any = { ...project };
    
    // Convert dates to YYYY-MM-DD (LocalDate on the backend — ADR-147).
    if (project.startDate) {
      updatePayload.startDate = project.startDate.toISOString().slice(0, 10);
    }
    if (project.endDate) {
      updatePayload.endDate = project.endDate.toISOString().slice(0, 10);
    }
    
    const updatedProject = await this.request<Project>(`/projects/${id}`, {
      method: 'PUT',
      body: JSON.stringify(updatePayload),
    });
    
    return {
      ...updatedProject,
      startDate: new Date(updatedProject.startDate),
      endDate: new Date(updatedProject.endDate),
      createdAt: new Date(updatedProject.createdAt),
      updatedAt: new Date(updatedProject.updatedAt)
    };
  }

  /**
   * Delete a project
   */
  async deleteProject(id: string): Promise<void> {
    return this.request<void>(`/projects/${id}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get projects by status
   */
  async getProjectsByStatus(status: string, condoId?: string): Promise<Project[]> {
    const url = condoId 
      ? `/projects/status/${status}?condoId=${condoId}`
      : `/projects/status/${status}`;
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get projects by priority
   */
  async getProjectsByPriority(priority: string, condoId?: string): Promise<Project[]> {
    const url = condoId 
      ? `/projects/priority/${priority}?condoId=${condoId}`
      : `/projects/priority/${priority}`;
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get projects by category
   */
  async getProjectsByCategory(category: string, condoId?: string): Promise<Project[]> {
    const url = condoId 
      ? `/projects/category/${category}?condoId=${condoId}`
      : `/projects/category/${category}`;
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get projects by year
   */
  async getProjectsByYear(year: number, condoId?: string): Promise<Project[]> {
    const url = condoId 
      ? `/projects/year/${year}?condoId=${condoId}`
      : `/projects/year/${year}`;
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get projects within date range
   */
  async getProjectsByDateRange(startDate: Date, endDate: Date, condoId?: string): Promise<Project[]> {
    const url = condoId 
      ? `/projects/date-range?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}&condoId=${condoId}`
      : `/projects/date-range?startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get project summary statistics
   */
  async getProjectSummary(condoId?: string): Promise<{
    totalProjects: number;
    activeProjects: number;
    completedProjects: number;
    totalBudget: number;
    totalActualCost: number;
    budgetVariance: number;
    projectsByStatus: Record<string, number>;
    projectsByPriority: Record<string, number>;
    projectsByCategory: Record<string, number>;
  }> {
    const url = condoId ? `/projects/summary?condoId=${condoId}` : '/projects/summary';
    return this.request(url);
  }

  /**
   * Get projects with budget overrun (actual cost > budget)
   */
  async getProjectsWithBudgetOverrun(condoId?: string): Promise<Project[]> {
    const url = condoId ? `/projects/over-budget?condoId=${condoId}` : '/projects/over-budget';
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get projects by responsible person
   */
  async getProjectsByResponsiblePerson(personId: string, condoId?: string): Promise<Project[]> {
    const url = condoId 
      ? `/projects/responsible/${personId}?condoId=${condoId}`
      : `/projects/responsible/${personId}`;
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Search projects by name or description
   */
  async searchProjects(query: string, condoId?: string): Promise<Project[]> {
    const url = condoId 
      ? `/projects/search?q=${encodeURIComponent(query)}&condoId=${condoId}`
      : `/projects/search?q=${encodeURIComponent(query)}`;
    const projects = await this.request<Project[]>(url);
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

  /**
   * Get project budget analytics
   */
  async getProjectBudgetAnalytics(condoId?: string): Promise<{
    totalBudget: number;
    totalActualCost: number;
    totalVariance: number;
    averageBudget: number;
    averageActualCost: number;
    projectsUnderBudget: number;
    projectsOverBudget: number;
    projectsOnBudget: number;
    budgetUtilizationRate: number;
  }> {
    const url = condoId ? `/projects/budget-analytics?condoId=${condoId}` : '/projects/budget-analytics';
    return this.request(url);
  }

  /**
   * Get project timeline analytics
   */
  async getProjectTimelineAnalytics(condoId?: string): Promise<{
    averageDuration: number;
    projectsCompletedOnTime: number;
    projectsDelayed: number;
    onTimeCompletionRate: number;
    averageDelayDays: number;
  }> {
    const url = condoId ? `/projects/timeline-analytics?condoId=${condoId}` : '/projects/timeline-analytics';
    return this.request(url);
  }

  /**
   * Bulk update project status
   */
  async bulkUpdateProjectStatus(projectIds: string[], status: string): Promise<Project[]> {
    const projects = await this.request<Project[]>('/projects/bulk-status', {
      method: 'PATCH',
      body: JSON.stringify({ projectIds, status }),
    });
    return projects.map(project => ({
      ...project,
      startDate: new Date(project.startDate),
      endDate: new Date(project.endDate),
      createdAt: new Date(project.createdAt),
      updatedAt: new Date(project.updatedAt)
    }));
  }

}
